import { computed, Injectable, signal, untracked } from "@angular/core";
import { findScopeInjector, type AngJSScope } from "../../main/compile.service";
import { S25ArraySort } from "../../util/core/s25.array.sort";
import { S25Assert } from "../../util/s25.assert";
import { S25Util } from "../../util/s25-util";
import type {
    EventFormObjectDetailsList,
    EventFormObjectDetailsRefreshData,
} from "./object-details/object.details.list.component";
import type { EventForm } from "./index";

/** AngularJS passes `itemTypeId` through `@` bindings and JSON, so it may arrive as a string. */
type ItemTypeIdInput = { itemTypeId?: number | string };

/**
 * eventually, this store should become a lot smarter than just holding a big object
 * and bumping one revision to force all dependent state to recalculate. for now though,
 * it just holds the model and serves as a centralized point for angJS to manually refresh
 * some Angular components.
 */
@Injectable()
export class EventFormStore {
    readonly #revision = signal(0);
    /**
     * read revision in a computed signal or effect to re-trigger
     * every time the form is updated
     */
    readonly revision = this.#revision.asReadonly();

    /**
     * the form model is a plain object that is mutated in place. signals rely on equality
     * checks, so any time you mutate the form, {@link bump} the store.
     */
    private formModel?: EventForm;

    private readonly detailsLists = new Map<number, EventFormObjectDetailsList>();

    readonly sortedOccurrences = computed(
        (): readonly EventForm.Occurrence[] => {
            const formModel = this.model();
            // TODO: do this with an effect, AFTER we safely destroy scope providers in angJS
            //  (see comment in CompileService.addProvidersToScope)
            untracked(() => formModel.updateObjOcc());
            formModel.occurrences.sort(S25ArraySort.shallowDates("evStartDt", "evEndDt"));
            return formModel.occurrences;
        },
        { equal: () => false },
    );

    /**
     * retrieve event form store from a given $scope (if it exists)
     */
    static fromScope($scope: AngJSScope | null | undefined): EventFormStore | undefined {
        return findScopeInjector($scope)?.get(EventFormStore, null, { optional: true }) ?? undefined;
    }

    /**
     * the big angJS bean. this reads the internal revision, so any time
     * {@link bump} is called, computed signals / effects will re-run
     */
    model(): EventForm {
        this.revision();
        S25Assert.isDefined(this.formModel, "EventFormStore.formModel was read before it was set");
        return this.formModel;
    }

    initialize(model: EventForm): void {
        S25Assert(!this.formModel || this.formModel === model, "EventFormStore.formModel is already set");
        this.formModel = model;
    }

    /** call this after the form model is mutated */
    bump(): void {
        this.#revision.update((count) => count + 1);
    }

    registerDetailsList(itemTypeId: number, list: EventFormObjectDetailsList): void {
        this.detailsLists.set(itemTypeId, list);
    }

    unregisterDetailsList(itemTypeId: number, list: EventFormObjectDetailsList): void {
        if (this.detailsLists.get(itemTypeId) === list) this.detailsLists.delete(itemTypeId);
    }

    /** Re-runs availability on the list for `itemTypeId`, or on every list when it is omitted. */
    refreshDetailsLists(data?: ItemTypeIdInput & EventFormObjectDetailsRefreshData): void {
        this.bump();
        const itemTypeId = S25Util.parseInt(data?.itemTypeId);
        if (S25Util.isDefined(itemTypeId)) {
            this.detailsLists.get(itemTypeId)?.refresh(data);
            return;
        }
        this.detailsLists.forEach((list) => list.refresh(data));
    }

    recreateDetailsLists(): void {
        this.bump();
        this.detailsLists.forEach((list) => list.recreate());
    }

    checkAvailForItems(data?: ItemTypeIdInput & { items?: { itemId: number }[] }): void {
        this.bump();
        const itemTypeId = S25Util.parseInt(data?.itemTypeId);
        if (!S25Util.isDefined(itemTypeId) || !data?.items) return;
        this.detailsLists.get(itemTypeId)?.checkAvailForItems(data.items);
    }
}
